import { useState } from 'react';

import { JOURNEY_KEYS } from './messagingLabels';
import { ErrorText, Field, Select } from './ui';
import { JOURNEY_NAMES } from '../../Lifecycle/shared/labels';

import Button from '@/components/Button';
import Modal from '@/components/Modal';
import { WorkerJourney, lifecycleErrorMessage } from '@/services/lifecycleAPI';
import styled from '@/styles';

const Content = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '18px 24px',
});

const Actions = styled('div', {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
  padding: '14px 24px',
  borderTop: '1px solid #EDEDED',
});

type Props = {
  journeys: WorkerJourney[];
  onSubmit: (journeyKey: string) => Promise<void>;
  onClose: () => void;
};

const EnrolModal = ({ journeys, onSubmit, onClose }: Props) => {
  const [journeyKey, setJourneyKey] = useState('J1');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const nameFor = (key: string) =>
    journeys.find((j) => j.journey_key === key)?.journey_name ||
    JOURNEY_NAMES[key];

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit(journeyKey);
    } catch (err) {
      setError(lifecycleErrorMessage(err, 'Could not enrol the worker.'));
      setSaving(false);
    }
  };

  return (
    <Modal
      disableClickout
      size="sm"
      title="Enrol in a journey"
      onRequestClose={saving ? undefined : onClose}
    >
      <Content>
        <span>
          The engine checks the journey fits the worker&apos;s stage. If it does
          not, the API says why.
        </span>
        <Field htmlFor="enrolJourneyKey">
          Journey
          <Select
            data-testid="enrolJourneySelect"
            id="enrolJourneyKey"
            value={journeyKey}
            onChange={(e) => {
              setJourneyKey(e.target.value);
              setError(null);
            }}
          >
            {JOURNEY_KEYS.map((key) => (
              <option key={key} value={key}>
                {`${key} · ${nameFor(key)}`}
              </option>
            ))}
          </Select>
        </Field>
        {error && (
          <div role="alert">
            <ErrorText>{error}</ErrorText>
          </div>
        )}
      </Content>
      <Actions>
        <Button
          a11yLabel="Cancel"
          appearance="outline"
          disabled={saving}
          label="Cancel"
          onClick={onClose}
        />
        <Button
          a11yLabel="Enrol"
          appearance="primary"
          data-testid="enrolConfirmBtn"
          isLoading={saving}
          label="Enrol"
          onClick={submit}
        />
      </Actions>
    </Modal>
  );
};

export default EnrolModal;
